<template>
  <div class="sri-page-header mt-4">
    <div class="search md-layout">
      <div class="md-layout-item md-size-30 md-small-size-100">
        <isis-autocomplete
          v-model="studentData.displayValue"
          :value.sync="studentData.value"
          :options="studentOpts"
          :refetchOptions="
            queryObj =>
              studentData.refetchOptions(studentData.displayValue, queryObj)
          "
          :fields="studentData.fields"
          :label="studentData.label"
          fetchOnOpen
        />
      </div>
      <div class="md-layout-item md-size-70 md-small-size-100">
        <md-button @click.prevent="button.action" class="md-info mr-6">
          {{ $t(button.title) }}
        </md-button>
        <div class="float-right">
          <md-button
            class="md-warning"
            @click.prevent="$emit('export-pdf')"
            v-if="showExport"
          >
            {{ $t("common.export") }}
          </md-button>
          <md-button
            v-if="canImport"
            class="md-success ml-3"
            @click.prevent="bulkActions.action"
          >
            {{ $t(bulkActions.content) }}
          </md-button>
        </div>
      </div>
      <import-xlsx-modal
        :name="importModalName"
        title="smi_sri.import_xlsx"
        :actions="importCSVModalActions"
        :formatImportedData="formatImportedData"
      >
        <template #template-desc>
          <p class="template-desc">
            {{ $t("common.download") }}
            <a :href="templateLinks">{{ $t("smi_sri.xlsx_template") }}</a>
            {{ $t("common.see_an_example_of_the_format_required") }}
          </p>
        </template>
      </import-xlsx-modal>
    </div>
  </div>
</template>

<script>
import ImportXlsxModal from "@/components/Base/ImportXlsxModal";
import { smiSriKeys } from "@/constants";
import { postFileToS3, saveUploadedLink } from "@/helpers/aws-s3";
import AWSConst from "@/constants/AWS";
import { service_paths } from "@/constants/Services";
import { initLoader, Message } from "@/helpers/utils";
import { IndividualAssessment } from "@/constants/Permissions";

const TEMPLATES_URL = process.env.VUE_APP_API_URL + service_paths.download;
export default {
  name: "sri-page-header",
  props: ["button", "data", "showExport"],
  components: { ImportXlsxModal },
  data() {
    return {
      importModalName: "importXLSX",
      studentOpts: [],
      studentData: {
        label: "smi_sri.search_by_full_name",
        value: "",
        displayValue: "",
        fields: {
          text: "full_name_id",
          value: "_id"
        },
        refetchOptions: (selectedValue, queryObj) => {
          return this.getStudentOpts(selectedValue, queryObj);
        }
      },
      bulkActions: {
        content: "common.import_file",
        action: () => {
          this.$modal.show(this.importModalName);
        }
      },
      formatImportedData: this.setSriSmiDataTable,
      importCSVModalActions: {
        primary: {
          title: "common.upload_and_continue",
          action: this.uploadFileToS3
        }
      },
      templateLinks: `${TEMPLATES_URL}/csv-template/sri-smi/basic`
    };
  },
  computed: {
    canImport() {
      return this.$can(
        IndividualAssessment.actions.import,
        IndividualAssessment.subject
      );
    }
  },
  methods: {
    getStudentOpts(value) {
      return this.$axios
        .get(
          service_paths.students + `/filter-with-role`,
          {
            limit: 10,
            search_key: value,
            fields: ["full_name", "student_code"]
          },
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )
        .then(({ data }) => {
          // get memorized selected when modal is opened
          this.studentOpts = data.data;
          this.studentOpts = this.studentOpts.map(student => ({
            full_name_id: student.full_name.concat(
              " - ",
              student.student_code || ""
            ),
            ...student
          }));
        });
    },
    setSriSmiDataTable(file) {
      return file?.map(student => ({
        [smiSriKeys.studentCode]: student[smiSriKeys.studentCode],
        [smiSriKeys.grade]: student[smiSriKeys.grade],
        [smiSriKeys.smiLastQuantileDate]:
          student[smiSriKeys.smiLastQuantileDate],
        [smiSriKeys.smiLastQuantile]: student[smiSriKeys.smiLastQuantile],
        [smiSriKeys.smiLastPerformanceLevel]:
          student[smiSriKeys.smiLastPerformanceLevel],
        [smiSriKeys.smiNCE]: student[smiSriKeys.smiNCE],
        [smiSriKeys.smiStanine]: student[smiSriKeys.smiStanine],
        [smiSriKeys.smiGrowthInDateRange]:
          student[smiSriKeys.smiGrowthInDateRange],
        [smiSriKeys.smiTestTaken]: student[smiSriKeys.smiTestTaken],
        [smiSriKeys.sriLastLexileDate]: student[smiSriKeys.sriLastLexileDate],
        [smiSriKeys.sriLastLexileScore]: student[smiSriKeys.sriLastLexileScore],
        [smiSriKeys.sriLastLexilePerormance]:
          student[smiSriKeys.sriLastLexilePerormance],
        [smiSriKeys.sriPercentile]: student[smiSriKeys.sriPercentile],
        [smiSriKeys.sriNCE]: student[smiSriKeys.sriNCE]
      }));
    },
    async uploadFileToS3(file) {
      const loader = initLoader();
      const s3FolderPath = AWSConst.S3_IMPORT_STUDENT_PATH;

      try {
        const savedLink = await postFileToS3(s3FolderPath, file);
        await saveUploadedLink(savedLink, `${service_paths.sri_smi}/import`);
        this.$modal.hide(this.importModalName);
        Message.success(this.$t("smi_sri.importXLSX"));
      } finally {
        loader.hide();
      }
    }
  },
  watch: {
    "studentData.value": {
      handler: function() {
        if (this.studentData.value) {
          this.$emit("studentValue", this.studentData.value);
        }
      }
    },
    "studentData.displayValue": {
      handler: function() {
        if (!this.studentData.displayValue) {
          this.$emit("studentValue", "");
        }
      }
    }
  }
};
</script>

<style lang="scss">
.sri-page-header {
  .md-autocomplete {
    label {
      z-index: auto;
    }
  }
}
</style>
